import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaFire, FaSwimmingPool, FaVolleyballBall, FaFutbol } from 'react-icons/fa';

const experienceData = [
  {
    title: 'Noches de Fogata',
    description: 'Comparte historias y relájate al calor del fuego bajo un manto de estrellas amazónicas.',
    icon: <FaFire className="text-4xl text-orange-500" />,
    image: '/images/experiencia-fogata.jpeg',
  },
  {
    title: 'Piscina Refrescante',
    description: 'Sumérgete y refréscate en nuestra piscina rodeada de la exuberante vegetación de la selva.',
    icon: <FaSwimmingPool className="text-4xl text-blue-500" />,
    image: '/images/experiencia-piscina.jpeg',
  },
  {
    title: 'Cancha de Vóley',
    description: 'Disfruta de un partido amistoso de vóley playa en nuestro campo de gras natural.',
    icon: <FaVolleyballBall className="text-4xl text-yellow-500" />,
    image: '/images/experiencia-voley.jpeg',
  },
  {
    title: 'Campo de Fútbol',
    description: 'Organiza un partido de fútbol con amigos o familiares en nuestro campo deportivo.',
    icon: <FaFutbol className="text-4xl text-green-500" />,
    image: '/images/experiencia-futbol.jpeg',
  },
];

const cardItemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

const ExperienceCard = ({ experience }) => {
  return (
    <motion.div
      variants={cardItemVariants}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="bg-white rounded-2xl overflow-hidden flex flex-col h-full"
      style={{
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.15)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)'}
    >
      <div className="relative overflow-hidden">
        <img
          src={experience.image}
          alt={experience.title}
          className="w-full h-44 sm:h-48 object-cover transform hover:scale-110 transition-transform duration-500"
        />
        <div
          className="absolute top-3 left-3 py-1.5 px-3 rounded-full z-10 flex items-center gap-1.5"
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <span className="text-lg">{experience.icon}</span>
        </div>
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="text-lg sm:text-xl font-playfair font-bold text-gray-800 mb-2">{experience.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{experience.description}</p>
      </div>
    </motion.div>
  );
};

const marqueeItems = [...experienceData, ...experienceData];

const Experiences = () => {
  const [paused, setPaused] = useState(false);

  return (
    <section id="experiencias" className="py-20 relative overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 80% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 50% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 65%)',
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 30% 70%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 60% 20%, rgba(196, 173, 121, 0.15) 0%, transparent 55%)',
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />
      <div className="container mx-auto px-4 relative" style={{ zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-sm uppercase tracking-[0.3em] mb-3 font-medium"
            style={{ color: '#c4ad79' }}
          >
            Experiencias
          </motion.p>
          <h2 className="text-4xl md:text-5xl font-playfair font-extrabold text-gray-900">Vive Sumaq Mayu</h2>
          <p className="mt-4 text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Más que una estadía, te ofrecemos momentos para recordar.
          </p>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            className="mx-auto mt-5"
            style={{
              width: '60px',
              height: '2px',
              background: '#c4ad79',
              transformOrigin: 'center',
              borderRadius: '2px',
            }}
          />
        </motion.div>

        {/* Mobile: Marquee infinito */}
        <style>{`
          @keyframes marquee-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
        <div
          className="md:hidden overflow-hidden"
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-8 z-10" style={{ background: 'linear-gradient(to right, #ffffff, transparent)' }} />
            <div className="absolute right-0 top-0 bottom-0 w-8 z-10" style={{ background: 'linear-gradient(to left, #ffffff, transparent)' }} />
            <div
              className="flex gap-4"
              style={{
                width: 'max-content',
                animation: 'marquee-scroll 20s linear infinite',
                animationPlayState: paused ? 'paused' : 'running',
              }}
            >
              {marqueeItems.map((exp, i) => (
                <div key={i} className="flex-shrink-0 w-[72vw]">
                  <div
                    className="bg-white rounded-2xl overflow-hidden flex flex-col h-full"
                    style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={exp.image}
                        alt={exp.title}
                        className="w-full h-44 object-cover"
                      />
                      <div
                        className="absolute top-3 left-3 py-1.5 px-3 rounded-full z-10"
                        style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                        }}
                      >
                        <span className="text-lg">{exp.icon}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-playfair font-bold text-gray-800 mb-1">{exp.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{exp.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Grid */}
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.15, delayChildren: 0.1 },
            },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-8"
        >
          {experienceData.map((exp) => (
            <ExperienceCard key={exp.title} experience={exp} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Experiences;
